import { CloseIcon, LocationIcon } from "../../Icons";
import { Drawer } from "antd";
import React, { useState, useEffect, useRef } from "react";
import LocationAutocomplete, {
  LocationPlacePayload,
} from "../index";
import { useUserLocation } from "../LocationContext";
import getUserLocation from "../../../utils/location";
import { showAlertModal } from "../../alertModal";
import constants from "../../../utils/constants";
import {
  areCitiesEquivalent,
  formatCityName,
  getCanonicalCitySlug,
} from "../../../config/cityData";

const MobileLocationSelector = () => {
  const [sortDrawerVisible, setSortDrawerVisible] = useState(false);
  const { userLocation, setUserLocation } = useUserLocation();
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  // Clearing a stale location must happen at most once per city pair. Without this
  // guard, anything that restores the location afterwards puts us in a set/clear
  // loop that React aborts with "Maximum update depth exceeded".
  const clearedForRef = useRef<string | null>(null);

  useEffect(() => {
    const updateCity = () => {
      const city = localStorage.getItem("selectedCity");
      setSelectedCity(city ? getCanonicalCitySlug(city) : null);
      if (
        userLocation?.city &&
        city &&
        !areCitiesEquivalent(userLocation.city, city)
      ) {
        const pair = `${userLocation.city}=>${city}`;
        if (clearedForRef.current !== pair) {
          clearedForRef.current = pair;
          setUserLocation(null);
        }
      } else if (userLocation?.city) {
        clearedForRef.current = null;
      }
    };
    updateCity();
    window.addEventListener("storage", updateCity);
    return () => window.removeEventListener("storage", updateCity);
  }, [userLocation?.city, setUserLocation]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const showSortDrawer = () => {
    setSortDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setSortDrawerVisible(false);
  };

  const handlePlaceSelected = (place: LocationPlacePayload | null) => {
    if (!place) return;
    setUserLocation(place);
    onCloseDrawer();
  };

  const getCurrentLocation = () => {
    getUserLocation(setUserLocation, showAlertModal({ isMobile }));
    onCloseDrawer();
  };

  const displayText =
    userLocation?.name &&
    (!userLocation.city ||
      !selectedCity ||
      areCitiesEquivalent(userLocation.city, selectedCity))
      ? userLocation.name
      : formatCityName(selectedCity || constants.DEFAULT_CITY);

  const cityLabel = selectedCity
    ? formatCityName(selectedCity)
    : formatCityName(constants.DEFAULT_CITY);

  return (
    <>
      <div
        onClick={showSortDrawer}
        className="location-container"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            showSortDrawer();
          }
        }}
      >
        <span>
          <LocationIcon size={20} color="#483285" />
        </span>
        <span className="location-text">{displayText}</span>
      </div>
      <Drawer
        title={
          <div className="mobile-location-drawer__title-row">
            <span className="mobile-location-drawer__title">Set your area</span>
            <button
              type="button"
              className="mobile-location-drawer__close"
              onClick={onCloseDrawer}
              aria-label="Close"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        }
        placement="bottom"
        closable={false}
        onClose={onCloseDrawer}
        open={sortDrawerVisible}
        height="72%"
        className="mobile-location-drawer"
        styles={{
          body: { paddingTop: 8 },
        }}
      >
        <div className="mobile-location-sheet">
          <p className="mobile-location-sheet__subtitle">
            Search a society, landmark or street in{" "}
            <strong>{cityLabel}</strong> — like food-delivery apps.
          </p>

          <div className="mobile-location-sheet__search-block">
            <span className="mobile-location-sheet__label">Search</span>
            <LocationAutocomplete
              onPlaceSelected={handlePlaceSelected}
              citySlugHint={selectedCity}
            />
          </div>

          <div className="mobile-location-sheet__divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="mobile-location-sheet__gps"
            onClick={getCurrentLocation}
          >
            <span className="mobile-location-sheet__gps-icon" aria-hidden>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="#483285"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                role="img"
              >
                <title>GPS</title>
                <path d="M13.58 10c0 1.977-1.603 3.58-3.58 3.58s-3.58-1.603-3.58-3.58c0-1.977 1.603-3.58 3.58-3.58v0c1.977 0 3.58 1.603 3.58 3.58v0zM20 9.52v0.96c0 0.265-0.215 0.48-0.48 0.48v0h-1.72c-0.447 3.584-3.256 6.393-6.802 6.836l-0.038 0.004v1.72c0 0.265-0.215 0.48-0.48 0.48v0h-0.96c-0.265 0-0.48-0.215-0.48-0.48v0-1.72c-3.575-0.455-6.375-3.262-6.816-6.802l-0.004-0.038h-1.74c-0.265 0-0.48-0.215-0.48-0.48v0-0.96c0-0.265 0.215-0.48 0.48-0.48v0h1.74c0.445-3.578 3.245-6.385 6.781-6.836l0.039-0.004v-1.72c0-0.265 0.215-0.48 0.48-0.48v0h0.96c0.265 0 0.48 0.215 0.48 0.48v0 1.72c3.584 0.447 6.393 3.256 6.836 6.802l0.004 0.038h1.72c0.265 0 0.48 0.215 0.48 0.48v0zM15.96 10c0-3.292-2.668-5.96-5.96-5.96s-5.96 2.668-5.96 5.96c0 3.292 2.668 5.96 5.96 5.96v0c3.292 0 5.96-2.668 5.96-5.96v0z" />
              </svg>
            </span>
            <span className="mobile-location-sheet__gps-text">
              <span className="mobile-location-sheet__gps-title">
                Use current location
              </span>
              <span className="mobile-location-sheet__gps-sub">Using GPS</span>
            </span>
          </button>
        </div>
      </Drawer>
    </>
  );
};

export default MobileLocationSelector;
